import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getContainer } from "../../ioc/IOCSetup";
import { MetricsValuePanel } from "../metrics/MetricsValuePanel";
import Big from 'big.js';
import { MetricHighChart } from "../metrics/MetricHighChart";
import { MetricsSingleValuePanel } from "../metrics/MetricSingleValuePanel";
import { KycStatus, Level, Settings, TimeFrame } from "../../http/protocol";

// Function to set item with expiry
function setItemWithExpiry(key: string, value: string, ttl?: number) {
  const now = new Date();
  const item = {
      value: value,
      expiry: now.getTime() + ( ttl ? ttl : 10 * 60 * 1000),  // ttl is in milliseconds
  }
  localStorage.setItem(key, JSON.stringify(item));
}

// Function to get item and check expiry
function getItemWithExpiry(key: string) {
  const itemStr = localStorage.getItem(key);

  // if the item doesn't exist, return null
  if (!itemStr) {
      return null;
  }

  const item = JSON.parse(itemStr);
  const now = new Date();

  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
      // If the item is expired, delete the item from storage and return null
      localStorage.removeItem(key);
      return null;
  }
  return item.value;
}


const PendingWithdraws: React.FC<{}> = () => {
  const paymentService = getContainer().getPaymentService();
  const [pendingWithdraws, setPendingWithdraws] = useState<number>(0);

  async function loadData() {
    let pendingListRequest: any = { types: ["WITHDRAWAL"], statuses: ["PENDING", "INITIATED"], limit: 100 };
    await paymentService.list(pendingListRequest)
      .then(pendingWithdrawsResponse => {
        setPendingWithdraws(pendingWithdrawsResponse && pendingWithdrawsResponse.items ? pendingWithdrawsResponse.items.length : 0);
        console.log(pendingWithdrawsResponse);
        console.log(pendingWithdraws);
        console.log("pendingWithdraws=" + pendingWithdraws);
      })
      .catch(e => {
        console.error("Error gettting paymentService List endpoint Pending Withdraws");
        setPendingWithdraws(NaN);
      });

  }

  useEffect(() => {
    loadData();
  }, [])

  return (
    <>
      <MetricsSingleValuePanel
        header={""}
        mainTitle={pendingWithdraws && pendingWithdraws > 1 ? "Pending Withdrawals" : "Pending Withdrawal"}
        mainValue={pendingWithdraws && pendingWithdraws}
        type={pendingWithdraws && pendingWithdraws > 0 ? "WARNING" : "OK"}
        navTo={"/payments"}
        listRequest={{ types: ["WITHDRAWAL"], statuses: ["PENDING", "INITIATED"] }}
      />
    </>
  )

}

const FailedPayments: React.FC<{}> = () => {
  const paymentService = getContainer().getPaymentService();
  const [failedPayments, setFailedPayments] = useState<number>(0);

  async function loadData() {
    let failedListRequest: any = { types: ["WITHDRAWAL", "DEPOSIT"], statuses: ["FAILED", "FAILED_PERMANENTLY"] };
    await paymentService.list(failedListRequest)
      .then(failedPaymentsResponse => {
        setFailedPayments(failedPaymentsResponse && failedPaymentsResponse.items ? failedPaymentsResponse.items.length : 0);
        console.log("FAILED=" + failedPayments);

      })
      .catch(e => {
        console.error("Error gettting paymentService List endpoint Failed");
        setFailedPayments(NaN);
      });
  }

  useEffect(() => {
    loadData();
  }, [])

  return (
    <>
      <MetricsSingleValuePanel
        header={""}
        mainTitle={"Failed Payments"}
        mainValue={failedPayments && failedPayments}
        type={failedPayments && failedPayments > 0 ? "ERROR" : "OK"}
        navTo={"/payments"}
        listRequest={{ types: ["WITHDRAWAL", "DEPOSIT"], statuses: ["FAILED", "FAILED_PERMANENTLY"] }} />
    </>
  )

}

const SystemErrors: React.FC<{}> = () => {
  const systemEventService = getContainer().getSystemEventService();
  const [errorEvents, setErrorEvents] = useState<number>(0);
  const [warningEvents, setWarningEvents] = useState<number>(0);

  async function loadData() {
    await systemEventService.getCounts()
      .then((errorEventsResponse: {infoEventsCount?: number, warnEventsCount?: number, errorEventsCount?: number}) => {
        let totalErrorEvents = 0;
        
        setErrorEvents(errorEventsResponse ? errorEventsResponse.errorEventsCount : 0);
        setWarningEvents(errorEventsResponse ? errorEventsResponse.warnEventsCount : 0);
        console.log("errorEvents=" + totalErrorEvents);
        console.log("warningEvents=" + warningEvents);
      })
      .catch(e => {
        console.error("Error gettting errorEvents count endpoint");
        setErrorEvents(NaN);
        setWarningEvents(NaN);
      });
  }

  useEffect(() => {
    loadData();
  }, [])

  return (
    <>
        <MetricsSingleValuePanel
          header={""}
          mainTitle={"Errors"}
          mainValue={errorEvents && errorEvents}
          type={errorEvents && errorEvents > 0 ? "ERROR" : "OK"}
          navTo={"/system-events"}
          listRequest={{ resolved: false, offset: 0, limit: 50, level: Level.ERROR }}
        />
        <MetricsSingleValuePanel
          header={""}
          mainTitle={"Warnings"}
          mainValue={warningEvents && warningEvents}
          type={warningEvents && warningEvents > 0 ? "ERROR" : "OK"}
          navTo={"/system-events"}
          listRequest={{ resolved: false, offset: 0, limit: 50, level: Level.WARN }}
        />
    </>
  )

}

const PendingKyc: React.FC<{}> = () => {
  const userService = getContainer().getUserService();
  const [kycStatusCount, setKycStatusCount] = useState<number>(0);
  async function loadData() {
    await userService.getUserKycStatusCount(KycStatus.REVIEW_NEEDED)
      .then(data => {
        setKycStatusCount(data);
      })
      .catch(e => {
        console.error("Error gettting userKycStatuys count endpoint");
        setKycStatusCount(NaN);
      });

  }

  useEffect(() => {
    loadData();
  }, [])

  return (
    <>
      <MetricsSingleValuePanel
        header={""}
        mainTitle={"KYC Review Needed"}
        mainValue={kycStatusCount && kycStatusCount}
        type={kycStatusCount && kycStatusCount > 0 ? "WARNING" : "OK"}
        navTo={"/users"}
        listRequest={{ kycStatus: [KycStatus.REVIEW_NEEDED] }}
      />
    </>
  )

}

const PendingCredits: React.FC<{}> = () => {
  const creditService = getContainer().getCreditService();
  const [pendingCredits, setPendingCredits] = useState<number>(0);

  async function loadData() {
    let pendingCreditsRequest: any = { status: "PENDING" };
    await creditService.list(pendingCreditsRequest)
      .then(creditResponse => {
        setPendingCredits(creditResponse && creditResponse.items ? creditResponse.items.length : 0);
      })
      .catch(e => {
        console.error("Error gettting pending credit List endpoint");
        setPendingCredits(NaN);
      })
  }

  useEffect(() => {
    loadData();
  }, [])

  return (
    <>
      <MetricsSingleValuePanel
        header={""}
        mainTitle={"Clawbacks"}
        mainValue={pendingCredits && pendingCredits}
        type={pendingCredits && pendingCredits > 0 ? "ERROR" : "OK"}
        navTo={"/credits"}
        listRequest={{ status: "PENDING" }}
      />
    </>
  )

}

function getDailyValues(data: any) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().slice(0, 10);

  let result: any = { [today]: 0, [yesterday]: 0 };
  if (data === null || data.points === undefined) {
    return result;
  }
  data.points.forEach((point: any) => {
    if (point.date === today) {
      result[today] = point.value;
    }
    if (point.date === yesterday) {
      result[yesterday] = point.value;
    }
  });

  return result;
}

function getPreviousAndCurrentValues(data: any, timeFrame : TimeFrame){
  
  const YESTERDAY = "YESTERDAY", TODAY = "TODAY";
  const LAST_MONTH = "LAST_MONTH", CURRENT_MONTH = "CURRENT_MONTH";

  const previousAndCurrentValues = {"previous": 0, "current": 0};

  if(timeFrame === TimeFrame.PREVIOUS_AND_CURRENT_DAY || timeFrame === TimeFrame.PREVIOUS_AND_CURRENT_MONTH){
    
    data.forEach( (element : any) => {
      if(element.key === YESTERDAY || element.key === LAST_MONTH){
        previousAndCurrentValues.previous = element.value;
      }else if(element.key === TODAY || element.key === CURRENT_MONTH){
        previousAndCurrentValues.current = element.value;
      }
    });
  }

  return previousAndCurrentValues;

}


const LoginSummary: React.FC<{}> = () => {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().slice(0, 10);
  const [dailyLogin, setDailyLogin] = useState({ [today]: 0, [yesterday]: 0 });
  const metricsService = getContainer().getMetricsService();

  async function loadData() {
    if (getItemWithExpiry("dailyLogin")) {
      setDailyLogin(JSON.parse(getItemWithExpiry("dailyLogin") || ""));
    } else {
      await metricsService.getUniqueUsersCount("LOGIN", yesterday, today)
        .then(dailyLoginCall => {
          let dailyValues = getDailyValues(dailyLoginCall);
          setDailyLogin(dailyValues);
          setItemWithExpiry("dailyLogin", JSON.stringify(dailyValues));
        })
        .catch(e => {
          console.error("Error gettting metrics for Daily Login endpoint");
          setDailyLogin({ [today]: NaN, [yesterday]: NaN });
          setItemWithExpiry("dailyLogin", JSON.stringify({ [today]: NaN, [yesterday]: NaN }));
        });
    }
  }

  useEffect(() => {
    loadData();
  }, [])

  return (
    <>
      <MetricsValuePanel
        header="Logins"
        mainTitle={"Today"}
        subTitle={"Yesterday"}
        mainValue={dailyLogin[today]}
        subValue={dailyLogin[yesterday]}
        currency={"Requests"}
        type="LOGIN" />

    </>
  )

}

const DepositSummary: React.FC<{}> = (currency: string, isNewCurrency: boolean) => {
  
  const metricsService = getContainer().getMetricsService();

  const TODAY = "TODAY", YESTERDAY = "YESTERDAY";
  const [dailyDeposit, setDailyDeposit] = useState({ [TODAY]: 0, [YESTERDAY]: 0 });
  

  async function loadData(currency: string, isNewCurrency: boolean) {

    if (getItemWithExpiry("dailyDeposit") && !isNewCurrency) {
      setDailyDeposit(JSON.parse(getItemWithExpiry("dailyDeposit") || ""));
    } else {
  
      await metricsService.getEventDailySum("DEPOSIT", TimeFrame.PREVIOUS_AND_CURRENT_DAY, currency, "MAIN")
        .then(depositData => {
  
          const depositValues = getPreviousAndCurrentValues(depositData.data, TimeFrame.PREVIOUS_AND_CURRENT_DAY);
          
          setDailyDeposit({[TODAY] : depositValues.current, [YESTERDAY] : depositValues.previous});
          setItemWithExpiry("dailyDeposit", JSON.stringify({[TODAY] : depositValues.current, [YESTERDAY] : depositValues.previous}));
        })
        .catch(e => {
          console.error("Error getting metrics for Deposit Daily endpoint");
          setDailyDeposit({[TODAY]: NaN, [YESTERDAY]: NaN});
          setItemWithExpiry("dailyDeposit", JSON.stringify({ [TODAY]: NaN, [YESTERDAY]: NaN }));
        });
    }
  }

  useEffect(() => {
    loadData(currency, isNewCurrency);
  }, [currency, isNewCurrency])

  return (
    <>
      <MetricsValuePanel
        header="Deposits"
        mainTitle={"Today"}
        subTitle={"Yesterday"}
        mainValue={dailyDeposit[TODAY]}
        subValue={dailyDeposit[YESTERDAY]}
        currency={currency}
        type="DEPOSIT" />
    </>
  )

}

const RevenueSummary: React.FC<{}> = (currency: string, isNewCurrency: boolean) => {

  const metricsService = getContainer().getMetricsService();

  const TODAY = "TODAY", YESTERDAY = "YESTERDAY";
  const CURRENT_MONTH = "CURRENT_MONTH", LAST_MONTH = "LAST_MONTH";

  const [dailyRevenue, setDailyRevenue] = useState({ [TODAY]: 0, [YESTERDAY]: 0 });
  const [monthlyRevenue, setMonthlyRevenue] = useState({ [CURRENT_MONTH]: 0, [LAST_MONTH]: 0 });

  async function loadData(currency: string, isNewCurrency: boolean) {

    if (getItemWithExpiry("dailyRevenue") && !isNewCurrency) {
      setDailyRevenue(JSON.parse(getItemWithExpiry("dailyRevenue") || ""));
    } else {
      
      await metricsService.getEventDailySum("REVENUE", TimeFrame.PREVIOUS_AND_CURRENT_DAY, currency, "MAIN")
        .then(dailyRevenueData => {

         const revenueData = getPreviousAndCurrentValues(dailyRevenueData.data, TimeFrame.PREVIOUS_AND_CURRENT_DAY);

          setDailyRevenue({[TODAY] : revenueData.current, [YESTERDAY] : revenueData.previous});
          setItemWithExpiry("dailyRevenue", JSON.stringify({[TODAY] : revenueData.current, [YESTERDAY] : revenueData.previous}));

        })
        .catch(e => {
          console.error("Error getting metrics for Daily Revenue endpoint");
          setDailyRevenue({ [TODAY]: NaN, [YESTERDAY]: NaN });
          setItemWithExpiry("dailyRevenue", JSON.stringify({ [TODAY]: NaN, [YESTERDAY]: NaN }));
        })
    }
    
    if (getItemWithExpiry("monthlyRevenue") && !isNewCurrency) {
      setMonthlyRevenue(JSON.parse(getItemWithExpiry("monthlyRevenue") || ""));
    } else {
      
      await metricsService.getEventDailySum("REVENUE", TimeFrame.PREVIOUS_AND_CURRENT_MONTH, currency, "MAIN")
        .then(monthlyRevenueData => {
          
          const revenueData = getPreviousAndCurrentValues(monthlyRevenueData.data, TimeFrame.PREVIOUS_AND_CURRENT_MONTH);

          setMonthlyRevenue({ [CURRENT_MONTH]: revenueData.current, [LAST_MONTH]: revenueData.previous });
          setItemWithExpiry("monthlyRevenue", JSON.stringify({ [CURRENT_MONTH]: revenueData.current, [LAST_MONTH]: revenueData.previous }));

        })
        .catch(e => {
          console.error("Error getting metrics for Monthly Revenue endpoint");
          setMonthlyRevenue({ [CURRENT_MONTH]: NaN, [LAST_MONTH]: NaN });
          setItemWithExpiry("monthlyRevenue", JSON.stringify({[CURRENT_MONTH]: NaN, [LAST_MONTH]: NaN }));
        });
    }
  }

  useEffect(() => {
    loadData(currency, isNewCurrency);
  }, [currency, isNewCurrency])

  return (
    <>
      <MetricsValuePanel
        header="Revenue"
        mainTitle={"Today"}
        subTitle={"Yesterday"}
        mainValue={dailyRevenue[TODAY]}
        subValue={dailyRevenue[YESTERDAY]}
        currency={currency}
        type="REVENUE" />

      <MetricsValuePanel
        header="Revenue"
        mainTitle={"This Month"}
        subTitle={"Last Month"}
        mainValue={monthlyRevenue[CURRENT_MONTH]}
        subValue={monthlyRevenue[LAST_MONTH]}
        currency={currency}
        type="REVENUE" />
    </>
  )

}

const RevenueyCurrentMonth: React.FC<{}> = (currency: string, isNewCurrency: boolean) => {

  const metricsService = getContainer().getMetricsService();
  const date: Date = new Date();
  const today = new Date(date.getFullYear(), date.getUTCMonth(), date.getDate()+1).toISOString().slice(0, 10);
  const startOfThisMonth = new Date(date.getFullYear(), date.getUTCMonth(), 1).toISOString().slice(0, 10);

  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([{ 'data': [], 'title': '' }]);

  async function loadData(currency: string, isNewCurrency: boolean) {
    if (getItemWithExpiry("monthlyData") && !isNewCurrency) {
      setMonthlyData(JSON.parse(getItemWithExpiry("monthlyData") || ""));
    } else {
      await metricsService.getSum("REVENUE", null, currency, startOfThisMonth, today, "MAIN")
        .then((monthlyRevenueCall: any) => {
          let dataMonthly = [{
            data: monthlyRevenueCall != null ? monthlyRevenueCall.points : [],
            title: "All"
          }];
          setMonthlyData(dataMonthly);
          setItemWithExpiry("monthlyData", JSON.stringify(dataMonthly));
        })
        .catch(e => {
          console.error("Error gettting metrics for Monthly Revenue endpoint");
          setMonthlyData([{ data: [], title: "All" }]);
          setItemWithExpiry("monthlyData", JSON.stringify([{ data: [], title: "All" }]));
        });
    }
  }

  useEffect(() => {
    loadData(currency, isNewCurrency)
  }, [currency, isNewCurrency])

  return (
    <>
      <MetricHighChart containerClassName="col-lg-12 col-xl-6" data={monthlyData}
        header={`Daily Revenue ${currency} - ${date.toLocaleString('default', { month: 'long' })}`} />
    </>
  )
}

const RevenueByDays: React.FC<{}> = (currency: string, isNewCurrency: boolean) => {

  const metricsService = getContainer().getMetricsService();
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 90)).toISOString().slice(0, 10);
  const [revenuePoker, setRevenuePoker] = useState<any>({ 'points': [] });
  const [revenueCasino, setRevenueCasino] = useState<any>({ 'points': [] });
  const [revenueRacebook, setRevenueRacebook] = useState<any>({ 'points': [] });
  const [revenueSportbook, setRevenueSportbook] = useState<any>({ 'points': [] });
  const [dataPrueba, setDataPrueba]: any = useState<MonthlyData[]>([{ 'data': [], 'title': '' }]);

  async function loadData(currency: string, isNewCurrency: boolean) {

    if (getItemWithExpiry("revenuePoker") && !isNewCurrency) {
      setRevenuePoker(JSON.parse(getItemWithExpiry("revenuePoker") || ""));
    } else {
      await metricsService.getSum("REVENUE", "POKER", currency, ninetyDaysAgo, today, "MAIN")
        .then(revenuePokerCall => {
          setRevenuePoker(revenuePokerCall);
          setItemWithExpiry("revenuePoker", JSON.stringify(revenuePokerCall));
        })
        .catch(e => {
          console.error("Error gettting metrics for Poker Revenue endpoint");
          setRevenuePoker({ points: [] });
          setItemWithExpiry("revenuePoker", JSON.stringify(revenuePoker));
        });
    }
    if (getItemWithExpiry("revenueCasino") && !isNewCurrency) {
      setRevenueCasino(JSON.parse(getItemWithExpiry("revenueCasino") || ""));
    } else {
      await metricsService.getSum("REVENUE", "CASINO", currency, ninetyDaysAgo, today, "MAIN")
        .then(revenueCasinoCall => {
          setRevenueCasino(revenueCasinoCall);
          setItemWithExpiry("revenueCasino", JSON.stringify(revenueCasinoCall));
        })
        .catch(e => {
          console.error("Error gettting metrics for Casino Revenue endpoint");
          setRevenueCasino({ points: [] });
          setItemWithExpiry("revenueCasino", JSON.stringify(revenueCasino));
        });
    }
    if (getItemWithExpiry("revenueRacebook") && !isNewCurrency) {
      setRevenueRacebook(JSON.parse(getItemWithExpiry("revenueRacebook") || ""));
    } else {
      await metricsService.getSum("REVENUE", "RACEBOOK", currency, ninetyDaysAgo, today, "MAIN")
        .then(revenueRacebookCall => {
          setRevenueRacebook(revenueRacebookCall);
          setItemWithExpiry("revenueRacebook", JSON.stringify(revenueRacebookCall));
        })
        .catch(e => {
          console.error("Error gettting metrics for Racebook Revenue endpoint");
          setRevenueRacebook({ points: [] });
          setItemWithExpiry("revenueRacebook", JSON.stringify(revenueRacebook));
        });
    }
    if (getItemWithExpiry("revenueSportbook") && !isNewCurrency) {
      setRevenueSportbook(JSON.parse(getItemWithExpiry("revenueSportbook") || ""));
    } else {
      await metricsService.getSum("REVENUE", "SPORTSBOOK", currency, ninetyDaysAgo, today, "MAIN")
        .then(revenueSportbookCall => {
          setRevenueSportbook(revenueSportbookCall);
          setItemWithExpiry("revenueSportbook", JSON.stringify(revenueSportbookCall));
        })
        .catch(e => {
          console.error("Error gettting metrics for Sportbook Revenue endpoint");
          setRevenueSportbook({ points: [] });
          setItemWithExpiry("revenueSportbook", JSON.stringify(revenueSportbook));
        });
    }

    let revenue1 = JSON.parse(getItemWithExpiry("revenuePoker") || "{}");
    let revenue2 = JSON.parse(getItemWithExpiry("revenueCasino") || "{}");
    let revenue3 = JSON.parse(getItemWithExpiry("revenueSportbook") || "{}");
    let revenue4 = JSON.parse(getItemWithExpiry("revenueRacebook") || "{}");
    setDataPrueba([
      { data: revenue1 != null ? revenue1.points : [], title: "Poker" },
      { data: revenue2 != null ? revenue2.points : [], title: "Casino" },
      { data: revenue3 != null ? revenue3.points : [], title: "Sportsbook" },
      { data: revenue4 != null ? revenue4.points : [], title: "Racebook" }]);

    console.log("dataPrueba", dataPrueba);
  }

  useEffect(() => {
    loadData(currency, isNewCurrency)
  }, [currency, isNewCurrency])

  return (
    <>
      <MetricHighChart containerClassName="col-lg-12 col-xl-6" data={dataPrueba}
        header={`Daily Revenue ${currency} by Product - 90 Days`} />
    </>
  )

}

const DaylyLogins: React.FC<{}> = ()=>{
  const metricsService = getContainer().getMetricsService();
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 90)).toISOString().slice(0, 10);
  const [dailyLoginGraph, setDailyLoginGraph] = useState<MonthlyData[]>([{ 'data': [], 'title': '' }]);

  async function loadData(){
    if (getItemWithExpiry("dailyLoginGraphData")) {
      setDailyLoginGraph(JSON.parse(getItemWithExpiry("dailyLoginGraphData") || ""));
    } else {
      await metricsService.getUniqueUsersCount("LOGIN", ninetyDaysAgo, today)
        .then((dailyLoginGraphDataCallNew: any) => {
          const dailyLoginGraphDataCall: any = [{ data: dailyLoginGraphDataCallNew != null ? dailyLoginGraphDataCallNew.points : [], title: "Login" }];
          setDailyLoginGraph(dailyLoginGraphDataCall);
          setItemWithExpiry("dailyLoginGraphData", JSON.stringify(dailyLoginGraphDataCall));

        })
        .catch(e => {
          console.error("Error gettting metrics for Sportbook Revenue endpoint");
          setItemWithExpiry("dailyLoginGraphData", "");
        });
    }
  }

  useEffect(()=>{
    loadData()
  },[])

  return(
    <>
      <MetricHighChart containerClassName="col-lg-12 col-xl-6" data={dailyLoginGraph}
          header={`Daily Logins - 90 Days`} />
    </>
  )
}
interface Props {
  currency: string;
}

interface MonthlyData {
  data: Array<{ date: string, value: string }>;
  title: string
}


const HomeMetrics: React.FC<Props> = ({ currency }) => {

  const [prevCurrency, setPrevCurrency] = useState(getItemWithExpiry("metricsPrevCurrency"));
  const settingsService = getContainer().getSettingsService();
  const internalSettingsService = getContainer().getInternalSettingsService();
  const [isNewCurrency, setIsNewCurrency] = useState(currency !== prevCurrency);
  const [kycOnWithdrawRequired, setKycOnWithdrawRequired] = useState<boolean>(false);
  const [creditEnabled, setCreditEnabled] = useState(false);

  async function getSettings() {
    if (getItemWithExpiry("kycOnWithdrawRequired")) {
      setKycOnWithdrawRequired(getItemWithExpiry("kycOnWithdrawRequired") == "true" || false);
    } else {
      await settingsService.getSettings()
        .then(settingsData => {
          setKycOnWithdrawRequired(settingsData.kycOnWithdrawRequired || false);
          setItemWithExpiry("kycOnWithdrawRequired", settingsData.kycOnWithdrawRequired? "true": "false");
        })
        .catch(e => {
          console.error("Error gettting settings List endpoint");
          setKycOnWithdrawRequired(false);
        });
    }
    if (getItemWithExpiry("creditEnabled")) {
      setCreditEnabled(getItemWithExpiry("creditEnabled") == "true" || false);
    } else {
      await internalSettingsService.getSettings()
        .then(internalSettingsData => {
          setItemWithExpiry("creditEnabled", internalSettingsData.creditEnabled ? "true" : "false");
          setCreditEnabled(internalSettingsData.creditEnabled || false);
        })
        .catch(e => {
          console.error("Error gettting internalSettings List endpoint");
          setCreditEnabled(false);
        });
    }
  }

  useEffect(() => {
    getSettings();
  }, [])

  useEffect(() => {
    setIsNewCurrency(prevCurrency !== currency);
    setPrevCurrency(currency)
    setItemWithExpiry("metricsPrevCurrency", currency);
  }, [currency]);

  return (
    <div>
      <div className="row">
        <PendingWithdraws />
        <FailedPayments />
        {kycOnWithdrawRequired && (<PendingKyc />)}
        <SystemErrors />
        {creditEnabled && (<PendingCredits />)}
      </div>
      <div className="row">
        {LoginSummary()}
        {DepositSummary(currency, isNewCurrency)}
        {RevenueSummary(currency, isNewCurrency)}
        {RevenueyCurrentMonth(currency, isNewCurrency)}
        {RevenueByDays(currency, isNewCurrency)}
        {DaylyLogins()}
      </div>
    </div>
  );
};

export default HomeMetrics;
